import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserDetailModel, UserModel } from 'countable@model';
import { CookieService } from 'ngx-cookie-service';
import { map, Observable, of, ReplaySubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { GenericResModel } from '../model/generic-res.model';
import { SetPasswordReqModel } from '../model/pasword/set-password-req.model';
import { SetPasswordResModel } from '../model/pasword/set-password-res.model';
import { TokenValidationResModel } from '../model/pasword/token-validation-res.model';
import { SignupReqModel } from '../model/register/signup-req.model';
import { SignupSsoInfoModel } from '../model/register/signup-sso-info.model';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public static readonly FIRM_SELECTED_HASH = 'firm-hash';
  private static readonly URI_AUTH = environment.api + '/auth';
  private static readonly URI_LOGOUT = environment.apiV1 + '/user/logout';
  private static readonly USER_KEY = 'auth-user';
  private static readonly USER_DETAIL_KEY = 'userDetails';
  public readonly subject = new ReplaySubject<boolean>(1);
  public readonly subjectUser = new ReplaySubject<UserModel>(1);
  public readonly subjectUserDetail = new ReplaySubject<UserDetailModel>(1);
  // this should use only by the interceptors
  public readonly subjectToken = new ReplaySubject<string>(1);
  public readonly subjectNewLogin = new ReplaySubject<boolean>(1);

  private jwtHelper = new JwtHelperService();
  private userDetail: UserDetailModel = null;
  private user: UserModel = null;
  private token: string = null;

  constructor(private http: HttpClient, private dialog: MatDialog, private router: Router, private cookie: CookieService) {
    if (localStorage.getItem(AuthService.USER_KEY) && localStorage.getItem(AuthService.USER_KEY) !== 'undefined') {
      this.setUser(JSON.parse(localStorage.getItem(AuthService.USER_KEY)), false);
    }
    if (localStorage.getItem(AuthService.USER_DETAIL_KEY) && localStorage.getItem(AuthService.USER_DETAIL_KEY) !== 'undefined') {
      this.setUserDetail(JSON.parse(localStorage.getItem(AuthService.USER_DETAIL_KEY)), false);
    }
    if (this.isAuthenticated()) {
      console.debug('auth service const, it is authenticated, propagating');
      this.subject.next(true);
    }
  }

  setUser(user: UserModel, updateStorage = true): void {
    if (!user) {
      console.error('User object is incorrect, I will not propagate it');
      return;
    }
    this.user = user;
    this.setToken(user.token);
    if (updateStorage) {
      localStorage.setItem(AuthService.FIRM_SELECTED_HASH, user.firm);
      localStorage.setItem(AuthService.USER_KEY, JSON.stringify(user));
      localStorage.setItem('isUserLoggedIn', 'true');
    }
    this.subjectUser.next(user);
  }

  setUserDetail(data: UserDetailModel, updateStorage = true): void {
    if (!data) {
      console.error('User detail object is incorrect, I will not propagate it');
      return;
    }
    if (updateStorage) {
      localStorage.setItem(AuthService.USER_DETAIL_KEY, JSON.stringify(data));
      localStorage.setItem('istimetrackingon', JSON.stringify(1));
    }
    this.userDetail = data;
    this.subjectUserDetail.next(data);
  }

  // for now, it only publishes the login
  public authenticated(isAuthenticated: boolean) {
    this.subject.next(isAuthenticated);
    this.subjectNewLogin.next(true);
  }

  public isAuthenticated(): boolean {
    return this.token && !this.jwtHelper.isTokenExpired(this.token);
  }

  /**
   * @deprecated use the subject instead
   */
  public getUser(): UserModel | null {
    return this.user;
  }

  /**
   * @deprecated use the subject instead
   */
  getUserDetail(): UserDetailModel | null {
    return this.userDetail;
  }

  logout(softLogout: boolean = false) {
    this.dialog.closeAll();
    localStorage.clear();
    sessionStorage.clear();
    this.user = null;
    this.userDetail = null;
    StorageService.reset();
    this.authenticated(false);
    if (!softLogout) {
      this.cookie.delete('email', '/login');
      this.cookie.delete('pass', '/login');
      this.router.navigate(['/login']).then();
    }
  }

  login(data: { password: string; captcha: string; username: string }): Observable<GenericResModel<UserModel>> {
    return this.http.post<GenericResModel<UserModel>>(AuthService.URI_AUTH + '/login', data);
  }

  loginWithGoogle(data: { token: string, captcha: string }): Observable<GenericResModel<UserModel>> {
    return this.http.post<GenericResModel<UserModel>>(AuthService.URI_AUTH + '/login-google', data);
  }

  loginWithSso(code: string, captcha: string): Observable<GenericResModel<UserModel>> {
    return this.http.post<GenericResModel<UserModel>>(AuthService.URI_AUTH + '/login-oauth', captcha, {headers: {'X-CODE': code}});
  }

  public prepareForRefreshAfterSwitchFirm(user: UserModel): Observable<boolean> {
    this.cleanTheStorage();
    this.setUser(user);
    return of(true);
  }

  public cleanTheStorage(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.user = null;
    this.userDetail = null;
    this.token = null;
    StorageService.reset();
  }

  public refreshUserProfile(): Observable<UserDetailModel> {
    return this.fetchUserProfile().pipe(
      map(res => {
        if (res) {
          this.setUserDetail(res.data);
        }
        return res.data;
      }));
  }

  public fetchUserProfile(): Observable<GenericResModel<UserDetailModel>> {
    return this.http.get<GenericResModel<UserDetailModel>>(environment.apiV1 + '/user/profile');
  }

  public signup(data: SignupReqModel): Observable<any> {
    return this.http.post<any>(AuthService.URI_AUTH + '/signup', data);
  }

  public prepareSignupWithSso(code: string, captcha: string) {
    return this.http.post<GenericResModel<SignupSsoInfoModel>>(AuthService.URI_AUTH + '/signup-prepare-sso', captcha, {headers: {'X-CODE': code}});
  }

  public prepareSignupWithGoogle(params: { token: string, captcha: string }) {
    return this.http.post<GenericResModel<SignupSsoInfoModel>>(AuthService.URI_AUTH + '/signup-prepare-sso-google', params);
  }

  public forgotPassword(email: string, token: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(AuthService.URI_AUTH + '/forgot-password', {
      'email': email,
      'captcha': token
    });
  }

  public validateActivationToken(token: string): Observable<TokenValidationResModel> {
    return this.http.post<TokenValidationResModel>(AuthService.URI_AUTH + '/token-validation', token);
  }

  public setPassword(req: SetPasswordReqModel): Observable<GenericResModel<void>> {
    return this.http.post<GenericResModel<void>>(AuthService.URI_AUTH + '/set-password', req);
  }

  public changePassword(req: { newPassword: string; captcha: string; oldPassword: string }): Observable<GenericResModel<SetPasswordResModel>> {
    return this.http.post<GenericResModel<SetPasswordResModel>>(environment.apiV1 + '/user/change-password', req);
  }

  public updateUserProfile(data: any): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(environment.apiV1 + '/user/profile', data);
  }

  private setToken(token: string): void {
    if (token) {
      this.token = token;
      this.subjectToken.next(token);
    }
  }
}
